/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 관심표명자 파일 업로드 컴포넌트
 * 버전 : 1.0
 * 파일명 : UploadFile.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-12-18
 * ================================================================ 
 * 수정일 : 2025-12-26, 강동형 : 업로드 오류 내용 조건문 추가, 데이터가 있을 때 조건 수정 필요
 * 수정일 : 2026-02-03, 강동형 : 엑셀 파일 업로드, 다운로드 기능 연결
 * ================================================================ */

import { Icon_ArrowBack } from '../common/CommonIcons'
import { useInterestedStore } from 'src/stores/InterestedStore'
import { ExtraCoverContent, ExtraCoverHeader, ExtraCoverWrapper } from '../common/ExtraCoverStyles'
import styled from 'styled-components'
import { AppImage } from 'src/assets/images/AppImage'
import { BottomButtonWrapper } from '../buttons/CommonButtons'
import { CancleButton, NextButton } from '../common/CommonStyled'
import { useModalStore } from 'src/stores/ModalStore'
import { Interest_ModalContent_Err } from './ModalInterest'
import { Common_ModalFooter_SimpleSingle, Common_ModalTitle_AlertCircle } from '../modals/CommonModal'
import { useEffect, useRef } from 'react'
import { openPicker } from 'src/utils/Etc'
import { getInterestedExcelTemplate, postInterestedExcelUpload } from 'src/api/Interested'
import { toast } from 'react-toastify'
import { LoadInterestedData } from './useSubmitInterestedFilter'

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    font-size: 14px;
    font-weight: 500;
    color: #757B82;
    margin-bottom: 20px;
`

const ContentBox = styled.div`
    display: flex;
    gap: 10px;

    font-size: 14px;
    font-weight: 500;
    color: #757B82;
`

const BoxWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    padding: 11px 20px;
    padding-right: 11px;
    border-radius: 5px;
    border: 1px solid #DDD;
`

const UploadButton = styled.button`
    display: flex;

    font-size: 16px;
    box-sizing: border-box;
    white-space: nowrap;
    border-radius: 5px;
    background-color: #fff;
    color: #229596;
    border: 1px solid #229596;
    padding: 13px 20px;
    line-height: 1;
`

const NoticeSpan = styled.span`
    display: block;
    white-space: pre-wrap;
    word-break: keep-all;
`

const UploadFile = () => {
    const setField = useInterestedStore(s=>s.setField)
    const tempFile = useInterestedStore(s=>s.tempFile)
    const reset = useInterestedStore(s=>s.reset)
    const modalSetField = useModalStore(s=>s.setField)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    useEffect(()=>{
        return(()=>{
            reset('tempFile')
        })
    },[])


    const onClickConfirm = async ()=>{
        //파일 가드
        if (!tempFile) {
            return
        }
        const res = await postInterestedExcelUpload(tempFile)
        if(res.ok){
            toast.success('업로드 완료')
            LoadInterestedData()
            return
        }
        else if (res.data.error_details){
            modalSetField('type','CENTER')
            modalSetField('width','calc(100vw - 20px)')
            modalSetField('padding','20px')
            modalSetField('title',<Common_ModalTitle_AlertCircle title={'업로드 오류'}/>)
            modalSetField('content',<Interest_ModalContent_Err data={res.data.error_details}/>)
            modalSetField('footer',<Common_ModalFooter_SimpleSingle action={()=>modalSetField('open',false)}/>)
            modalSetField('open',true)
        }
        else{
            modalSetField('type','CENTER')
            modalSetField('width','calc(100vw - 20px)')
            modalSetField('padding','20px')
            modalSetField('title',<Common_ModalTitle_AlertCircle title={'엑셀 파일 형식 오류'}/>)
            modalSetField('content',<Interest_ModalContent_Err/>)
            modalSetField('footer',<Common_ModalFooter_SimpleSingle action={()=>modalSetField('open',false)}/>)
            modalSetField('open',true)
        }
    }

    const onChangeFiles = (e:React.ChangeEvent<HTMLInputElement>)=>{
        const input = e.currentTarget
        const file = input.files?.[0] ?? null;

        if(!file) return
        setField('tempFile',file)
        input.value = '';
    }

    const onClickDownload = async ()=>{
        await getInterestedExcelTemplate()
    }

    return (
        <ExtraCoverWrapper>
            <ExtraCoverHeader>
                <div className="left" onClick={() => setField("isCoverdBy", null)}>
                    <Icon_ArrowBack />
                </div>
                <span className='title'>
                    관심 표명자 명단 업로드
                </span>
                <div className="right" />
            </ExtraCoverHeader>
            <ExtraCoverContent>
                <ContentContainer>
                    <span>
                        *업로드용 엑셀 양식 다운로드
                    </span>
                    <BoxWrapper onClick={onClickDownload} style={{cursor:'pointer'}}>
                        <span style={{fontSize:'16px'}}>
                            관심표명자 명단 양식
                        </span>
                        <AppImage name='IconDownLoad24Gray'/>
                    </BoxWrapper>
                </ContentContainer>
                <ContentContainer>
                    <span>
                        *엑셀 파일 첨부
                    </span>
                    <ContentBox>
                        <BoxWrapper style={{backgroundColor:'#F5F5F5'}}>
                            <span style={{fontSize:'16px'}}>
                                {tempFile ? (tempFile.name) :'엑셀 파일을 업로드 해주세요'}
                            </span>
                        </BoxWrapper>
                        <UploadButton onClick={()=>openPicker(fileInputRef.current)}>
                            파일 업로드
                        </UploadButton>
                    </ContentBox>
                </ContentContainer>
                    <ContentContainer>
                    <span>
                        *주의사항
                    </span>
                    <BoxWrapper>
                        <NoticeSpan>{`• 양식에 명시된 규칙을 지켜주세요. 규칙을 지키지 않으면
입력 결과가 달라질 수 있습니다.

• 다음 항목은 필수 입력 항목입니다. 해당 항목이 모두 입력
되지 않으면 명단 등록이 누락됩니다.

  • 학교 이름
  • 홍보 일자
  • 학생 이름
  • 최근상담일자
  • 연락처
  • 생년월일
  • 성별
  • 상태

• 오타가 발생할 경우 필터없이 등록되어 새로운 홍보 결과
가 생길 수 있습니다.`}
                        </NoticeSpan>
                    </BoxWrapper>
                </ContentContainer>
            </ExtraCoverContent>
            <BottomButtonWrapper>
                <CancleButton onClick={() => setField("isCoverdBy", null)}>취소</CancleButton>
                <NextButton onClick={onClickConfirm}>업로드</NextButton>
            </BottomButtonWrapper>
            <input ref={fileInputRef} type='file' accept='.xls,.xlsx' onChange={onChangeFiles} style={{display:'none'}}/>
        </ExtraCoverWrapper>
    )
}

export default UploadFile